import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  adminFetchPosts,
  createPost,
  updatePost,
  deletePost,
  uploadImage,
  apiError,
} from "../../lib/api";
import { BLOG_COVERS, BLOG_CATEGORIES } from "../../config/site";
import { sharePostUrl, WhatsAppIcon } from "../site/WhatsAppIcon";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { formatDate } from "../site/PostCard";

const EMPTY = { title: "", excerpt: "", content: "", category: "General", published: true };
const inputCls =
  "bg-paper-light border-border text-ink placeholder:text-[#4A4C4A]/50 focus-visible:ring-gold";

export const BlogManager = ({ token, onAuthFail }) => {
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [coverChoice, setCoverChoice] = useState("/assets/masterplan.webp");
  const [customUrl, setCustomUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadImage(token, file);
      setCoverChoice("__custom");
      setCustomUrl(data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(apiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const { data } = await adminFetchPosts(token);
      setPosts(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(apiError(err, "Could not load posts"));
    }
  }, [token, onAuthFail]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setCoverChoice("/assets/masterplan.webp");
    setCustomUrl("");
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, excerpt: p.excerpt || "", content: p.content, category: p.category || "General", published: p.published });
    if (p.cover && BLOG_COVERS.some((c) => c.value === p.cover)) {
      setCoverChoice(p.cover);
      setCustomUrl("");
    } else if (p.cover) {
      setCoverChoice("__custom");
      setCustomUrl(p.cover);
    } else {
      setCoverChoice("");
      setCustomUrl("");
    }
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const cover = coverChoice === "__custom" ? customUrl.trim() : coverChoice || null;
    try {
      if (editing) {
        await updatePost(token, editing.id, { ...form, cover });
        toast.success("Article updated");
      } else {
        await createPost(token, { ...form, cover });
        toast.success("Article published");
      }
      setOpen(false);
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await deletePost(token, p.id);
      toast.success("Article deleted");
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(apiError(err, "Delete failed"));
    }
  };

  return (
    <div className="mt-6" data-testid="blog-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#4A4C4A]">
          Publish daily updates — policy explainers, construction progress, market notes.
        </p>
        <Button
          onClick={openNew}
          className="bg-forest text-paper hover:bg-forest-light transition-colors duration-200"
          data-testid="blog-new-button"
        >
          <Plus className="mr-2 h-4 w-4" /> New Article
        </Button>
      </div>

      {posts === null ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gold" />
        </div>
      ) : posts.length === 0 ? (
        <p className="mt-6 border border-dashed border-border bg-paper-light p-10 text-center text-sm text-[#4A4C4A]" data-testid="blog-admin-empty">
          No articles yet. Click "New Article" to publish your first one.
        </p>
      ) : (
        <div className="mt-6 space-y-3">
          {posts.map((p) => (
            <div
              key={p.id}
              className="flex flex-wrap items-center gap-4 border border-border bg-paper-light px-5 py-4"
              data-testid={`blog-row-${p.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-lg font-medium text-forest">{p.title}</p>
                <p className="mt-0.5 text-xs text-[#4A4C4A]">
                  {formatDate(p.created_at)} · /blog/{p.slug}
                </p>
              </div>
              <span
                className={`px-2.5 py-1 text-[11px] font-medium ${
                  p.published ? "bg-forest text-paper" : "bg-sand text-[#4A4C4A]"
                }`}
                data-testid={`blog-status-${p.id}`}
              >
                {p.published ? "Published" : "Draft"}
              </span>
              <div className="flex items-center gap-1">
                <a
                  href={sharePostUrl(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on WhatsApp"
                  title="Share on WhatsApp"
                  data-testid={`blog-share-${p.id}`}
                  className="p-2 text-[#25D366] hover:text-[#128C4B] transition-colors duration-150"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                </a>
                <button
                  aria-label="Edit article"
                  onClick={() => openEdit(p)}
                  data-testid={`blog-edit-${p.id}`}
                  className="p-2 text-[#4A4C4A]/60 hover:text-forest transition-colors duration-150"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  aria-label="Delete article"
                  onClick={() => handleDelete(p)}
                  data-testid={`blog-delete-${p.id}`}
                  className="p-2 text-[#4A4C4A]/60 hover:text-red-700 transition-colors duration-150"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-paper-light border-border text-ink sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-forest">
              {editing ? "Edit article" : "New article"}
            </DialogTitle>
            <DialogDescription className="text-[#4A4C4A]">
              Simple formatting: blank line = new paragraph, <code>## Heading</code>,{" "}
              <code>- bullet</code>, <code>**bold**</code>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="blog-form">
            <div className="space-y-1.5">
              <Label htmlFor="blog-title">Title *</Label>
              <Input
                id="blog-title"
                data-testid="blog-title-input"
                required
                minLength={3}
                className={`${inputCls} h-11`}
                placeholder="e.g. Haryana MSME Policy 2026 — what it means for you"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-excerpt">Excerpt</Label>
              <Textarea
                id="blog-excerpt"
                data-testid="blog-excerpt-input"
                rows={2}
                maxLength={400}
                className={inputCls}
                placeholder="One-line summary shown on cards and search results"
                value={form.excerpt}
                onChange={(e) => set("excerpt", e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="blog-category-select" className={`${inputCls} h-11`}>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-paper-light">
                    {BLOG_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#4A4C4A]/70">
                  "Construction update" posts also appear in the Construction Diary on the homepage.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Cover image</Label>
                <Select value={coverChoice} onValueChange={setCoverChoice}>
                  <SelectTrigger data-testid="blog-cover-select" className={`${inputCls} h-11`}>
                    <SelectValue placeholder="Choose a cover" />
                  </SelectTrigger>
                  <SelectContent className="bg-paper-light">
                    {BLOG_COVERS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom">Custom image URL…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {coverChoice === "__custom" && (
                <div className="space-y-1.5">
                  <Label htmlFor="blog-cover-url">Image URL</Label>
                  <Input
                    id="blog-cover-url"
                    data-testid="blog-cover-url-input"
                    className={`${inputCls} h-11`}
                    placeholder="https://…"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
                data-testid="blog-cover-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="blog-cover-upload-button"
                className="border-forest text-forest hover:bg-forest hover:text-paper transition-colors duration-150"
              >
                {uploading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                {uploading ? "Uploading…" : "Upload photo from device"}
              </Button>
              <p className="mt-1.5 text-[11px] text-[#4A4C4A]/70">
                Photos are auto-compressed to web size (WebP, max 10 MB).
              </p>
            </div>
            {(coverChoice === "__custom" ? customUrl : coverChoice) && (
              <img
                src={coverChoice === "__custom" ? customUrl : coverChoice}
                alt="Cover preview"
                className="h-32 w-full border border-border object-cover"
              />
            )}
            <div className="space-y-1.5">
              <Label htmlFor="blog-content">Article *</Label>
              <Textarea
                id="blog-content"
                data-testid="blog-content-input"
                required
                minLength={10}
                rows={12}
                className={`${inputCls} font-mono text-sm leading-relaxed`}
                placeholder={"Write the article here…\n\n## A section heading\n\n- A bullet point\n\nA paragraph with **bold text**."}
                value={form.content}
                onChange={(e) => set("content", e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2.5 text-sm text-[#4A4C4A]" htmlFor="blog-published">
              <input
                id="blog-published"
                type="checkbox"
                data-testid="blog-published-checkbox"
                checked={form.published}
                onChange={(e) => set("published", e.target.checked)}
                className="h-4 w-4 accent-[#1C3327]"
              />
              Publish immediately (uncheck to save as draft)
            </label>
            <Button
              type="submit"
              disabled={saving}
              data-testid="blog-save-button"
              className="w-full h-11 bg-forest text-paper hover:bg-forest-light transition-colors duration-200"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Save changes" : form.published ? "Publish article" : "Save draft"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
